import { Button } from "@/shared/components";
import { Table } from "@tanstack/react-table";
import { BooleanFilter, DataTableBooleanFilter } from "./data-table-boolean-filter";
import { DataTableClearAllFiltersButton } from "./data-table-clear-all-filters-button";
import { ColumnTextFilter, DataTableColumnTextFilter } from "./data-table-column-text-filter";
import { DataTableColumnVisibility } from "./data-table-column-visibility";
import type { DataTableFeatures } from "./data-table-features";
import { DataTableGlobalFilter, GlobalFilter } from "./data-table-global-filter";
import { DataTableMultiSelectFilter, MultiSelectFilter } from "./data-table-multi-select-filter";

export interface Filters<TData extends Record<string, unknown>> {
    globalFilter?: GlobalFilter<TData>;
    columnTextFilters?: ColumnTextFilter[];
    multiSelectFilters?: MultiSelectFilter[];
    booleanFilters?: BooleanFilter[];
}

export type DataTableToolbarOptions<TData extends Record<string, unknown>> = {
    filters?: Filters<TData>;
    columnVisibility?: {
        showMenu: boolean;
        defaultValues?: {
            [columnName: string]: boolean;
        };
    };
    actions?: {
        label: string;
        onClick: () => void;
        "data-testid"?: string;
    }[];
};

type DataTableToolbarProps<TData extends Record<string, unknown>> = {
    table: Table<DataTableFeatures, TData>;
    toolbarOptions: DataTableToolbarOptions<TData>;
};

export function DataTableToolbar<TData extends Record<string, unknown>>({ table, toolbarOptions }: DataTableToolbarProps<TData>) {
    const { filters } = toolbarOptions;
    return (
        <div className="flex items-start gap-2 py-4">
            <div className="flex flex-wrap gap-2">
                {filters?.globalFilter && (
                    <DataTableGlobalFilter
                        globalFilter={String(table.store.state.globalFilter ?? "")}
                        setGlobalFilter={(value) => table.setGlobalFilter(value)}
                        placeholder={filters.globalFilter.placeholder}
                    />
                )}
                {filters?.columnTextFilters && (
                    <>
                        {filters?.columnTextFilters.map((filterColumn) => (
                            <DataTableColumnTextFilter
                                key={filterColumn.column}
                                table={table}
                                column={filterColumn.column}
                                placeholder={filterColumn.placeholder}
                            />
                        ))}
                    </>
                )}
                {filters?.multiSelectFilters && (
                    <>
                        {filters?.multiSelectFilters.map((filterColumn) => (
                            <DataTableMultiSelectFilter
                                key={filterColumn.column}
                                column={table.getColumn(filterColumn.column)}
                                title={filterColumn.title}
                                options={filterColumn.options}
                            />
                        ))}
                    </>
                )}
                {filters?.booleanFilters && (
                    <>
                        {filters?.booleanFilters.map((filterColumn) => (
                            <DataTableBooleanFilter
                                column={table.getColumn(filterColumn.column)}
                                title={filterColumn.title}
                                key={filterColumn.column}
                                data-testid={filterColumn["data-testid"]}
                            />
                        ))}
                    </>
                )}
                <DataTableClearAllFiltersButton table={table} />
            </div>
            <div className="ml-auto flex gap-2">
                {toolbarOptions.actions && (
                    <>
                        {toolbarOptions.actions.map((action) => (
                            <Button
                                key={action.label}
                                onClick={action.onClick}
                                className="shrink-0"
                                variant="secondary"
                                data-testid={action["data-testid"]}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </>
                )}
                {toolbarOptions.columnVisibility?.showMenu && <DataTableColumnVisibility table={table} />}
            </div>
        </div>
    );
}
